/**
 * constants.ts — the kitchen regime definition (v2: PICKUP/SKIP reframe).
 *
 * Ported from `_proto_kitchen_v2.py` (lines 100-300). The regime change from
 * v1: items are NOT obstacles. The agent moves freely (BFS to the nearest
 * undecided item — a fixed policy, nothing learned) and makes ONE binary
 * decision per item: PICKUP or SKIP. The non-linearity (item × context
 * conjunction) lives in the same place it always did — raw features + a
 * tree's axis-aligned splits — but the action is now a clean binary call
 * instead of a tangled move-and-maybe-collect.
 *
 *  v1:  8-direction moves; stepping onto an item auto-collected it; to skip
 *       a bad item the agent had to ROUTE AROUND it (items as obstacles) →
 *       three-role MoE (gate → exploiter → navigator) + visited mask + TS
 *       direction bandit.
 *  v2:  binary {PICKUP, SKIP} per item; movement is a fixed BFS walk; the
 *       ONLY learned decision is pickup vs skip. No gate, no navigator, no
 *       visited mask. The non-linearity is isolated and the foil contrast
 *       is sharper.
 */

// ── Grid ───────────────────────────────────────────────────────────────
export const GRID = 15;
export const START_R = Math.floor(GRID / 2); // 7
export const START_C = Math.floor(GRID / 2); // 7
export const EMPTY = -1; // cell value: EMPTY or item-id in [0, K)

// ── Actions: BINARY pickup/skip (the v2 reframe) ───────────────────────
// There is no DIRECTIONS table and no 8-way movement choice in v2. The
// agent's action is BINARY {SKIP=0, PICKUP=1}. Movement between items is
// a FIXED BFS-to-nearest-undecided-item policy, not a learned decision.
export const SKIP = 0;
export const PICKUP = 1;
export const N_ACTIONS = 2;

// ── Items + contexts (the ground-truth ruleset the agent must learn) ───
export const K = 8; // number of distinct item types
export const COFFEE = 0;
export const BEER = 1;
export const EGGS = 2;
export const FLOUR = 3;
export const SUGAR = 4;
export const TOAST = 5;
export const MILK = 6;
export const JUICE = 7;
export const ITEM_NAMES: ReadonlyArray<string> = [
  'coffee', 'beer', 'eggs', 'flour', 'sugar', 'toast', 'milk', 'juice',
];
export const ITEM_EMOJI: ReadonlyArray<string> = [
  '☕', '🍺', '🥚', '🌾', '🍯', '🍞', '🥛', '🧃',
];

export const MORNING = 0;
export const EVENING = 1;
export const N_CTX = 2;
export const CTX_NAMES: ReadonlyArray<string> = ['morning', 'evening'];

// Per-item value by context (rows = K items, cols = N_CTX). 4 flip items
// at ±3 (coffee/beer/eggs/milk) + 4 stable +1 items. Row order MUST match
// ITEM_NAMES: the flips are at indices 0,1,2,6 (coffee/beer/eggs/milk);
// flour/sugar/toast/juice are the stables. cap=K/2 forces the agent to be
// SELECTIVE; the optimum is 2 correct-flip + 2 stables = +8. A linear head
// that cannot see the item×ctx conjunction averages each flip item's
// weight to ~0 and falls back to the 4 stables for +4 = 0.5x. The tree
// splits on item-coffee then ctx-morning and nails +3.
export const ITEM_VALUE: ReadonlyArray<ReadonlyArray<number>> = [
  [+3.0, -3.0], // coffee — morning flip
  [-3.0, +3.0], // beer   — evening flip
  [+3.0, -3.0], // eggs   — breakfast flip
  [+1.0, +1.0], // flour  — stable
  [+1.0, +1.0], // sugar  — stable
  [+1.0, +1.0], // toast  — stable
  [-3.0, +3.0], // milk   — evening flip
  [+1.0, +1.0], // juice  — stable
];

// Forbidden pairs / recipes deferred — v2 isolates ONE non-linearity (the
// context flip), same as v1. The data structures are kept for symmetry.
export const FORBIDDEN_PAIRS: ReadonlyArray<readonly [number, number, number]> = [];
export const RECIPES: ReadonlyArray<{
  items: ReadonlySet<number>;
  bonus: number;
  name: string;
}> = [];

// ── Reward weights ─────────────────────────────────────────────────────
// v2 has NO step penalty: movement is free and deterministic (BFS). The
// reward stream is exactly the per-decision basket-score delta, EXCEPT for
// duplicate pickups (see R_DUPLICATE).
export const R_SKIP = 0;
// Picking up an item whose type is ALREADY in the basket is a wasteful
// no-op (the basket is a set — the bit is already set, scoreBasket doesn't
// change). Without a penalty the agent gets reward 0 for both pickup and
// skip on a duplicate, so it has NO signal to learn "skip already-collected
// types." R_DUPLICATE gives that signal: a duplicate pickup costs -0.5.
// The tree learns the basket[item]=1 ∧ action=pickup → -0.5 conjunction
// and skips duplicates; the linear foil provably cannot isolate that
// conjunction (same dot-product argument as the context flip), so the
// thesis gets stronger. Magnitude is deliberately below the flip items'
// ±3 so it doesn't wash out the context signal.
export const R_DUPLICATE = -0.5;
export const MAX_STEPS = 60; // episode cap (item-decisions; generous)
export const BASKET_CAP = Math.floor(K / 2); // 4 — forces selectivity

// ── Feature layout (20-dim 2-arm contextual bandit) ────────────────────
// Two rows per decision (SKIP=0, PICKUP=1), identical except action one-hot:
//   [ item:    K        ]   8 cols (one-hot of the encountered item)
//   [ basket:  K        ]   8 cols (one-hot of current basket)
//   [ ctx:     N_CTX    ]   2 cols (one-hot of current context)
//   [ action:  N_ACTIONS]   2 cols (one-hot: skip / pickup)
// Total: 8 + 8 + 2 + 2 = 20.
// NO pre-computed [item × ctx] columns — they would let the linear foil
// solve the problem by direct lookup and the headline negative result
// collapses.
export const ITEM_FEATURES = K; // 8
export const BASKET_FEATURES = K; // 8
export const CTX_FEATURES = N_CTX; // 2
export const ACTION_FEATURES = N_ACTIONS; // 2
export const OFF_ITEM = 0;
export const OFF_BASKET = OFF_ITEM + ITEM_FEATURES; // 8
export const OFF_CTX = OFF_BASKET + BASKET_FEATURES; // 16
export const OFF_ACTION = OFF_CTX + CTX_FEATURES; // 18
export const N_FEATURES = OFF_ACTION + ACTION_FEATURES; // 20

// ── Bandit hyperparameters ─────────────────────────────────────────────
export const WINDOW_PER_DAY = 300;
export const LONG_CAP = 800;
export const N_TREES = 40;
// v2: decisions are dense (1 per item), so cold-start is short. 8 decisions
// = ~1 episode of mixed data.
export const COLD_START_K = 8;
export const REFIT_EVERY = 8;
export const RIDGE_LAMBDA = 1.0; // linear-foil ridge regularization

// ── Day schedule (the discrete-theme evaluation) ───────────────────────
export const DAY_THEMES = ['morning', 'evening', 'all_day'] as const;
export const HEADLINE_THEME = 'all_day';
export const N_WORLDS_PER_DAY = 12;
export const N_TRAIN_WORLDS = 12;
export const N_EVAL_WORLDS = 12;
export const FLIP_AT_STEP = 25; // H3: context flips at this decision index

export type ThemeName = 'morning' | 'evening' | 'all_day';
export type ArmMode = 'ts' | 'greedy' | 'linear' | 'random';
export type DriftStatus = 'warmup' | 'ok' | 'warning' | 'drift';
